import {flatten, map, pipe, split} from 'ramda'
import {v4 as uuid} from 'uuid'

import {Room as BoVioRoom} from '../../../api-types/bovio/response/bookings'
import {
  Room,
  RoomConfigurationType,
  RoomSplitType,
  UniqueRoomSplitType
} from '../../types/RoomConfiguration'
import {
  CHILD_SEPARATOR,
  DEFAULT_ADULTS_PER_ROOM,
  DEFAULT_ROOMS,
  GUEST_SEPARATOR,
  ROOM_SEPARATOR
} from './config'

/**
 * Converts a rooms string to an array of room split configurations.
 * @param rooms - String representation of rooms
 * @returns Array of room split configurations
 */
export const roomsStringToRoomsSplit = (rooms: string) =>
  pipe(split(ROOM_SEPARATOR), map(roomToRoomSplit))(rooms || DEFAULT_ROOMS)

const roomToRoomSplit = (room: string): RoomSplitType => {
  const [adults, children] = room.split(GUEST_SEPARATOR)

  return {
    adults: Number(adults),
    children: children
      ? pipe(split(CHILD_SEPARATOR), map(Number))(children)
      : []
  }
}

/**
 * Converts room split array to a configuration object with totals.
 * @param roomsSplit - Array of room split configurations
 * @returns Room configuration object with counts
 */
export const roomsSplitToConfigurationObject = (
  roomsSplit: RoomSplitType[]
): RoomConfigurationType => {
  const numberOfAdults = roomsSplit.reduce((acc, room) => acc + room.adults, 0)

  const numberOfChildren = roomsSplit.reduce(
    (acc, room) => acc + (room.children ? room.children.length : 0),
    0
  )

  return {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms: roomsSplit.length,
    roomsSplit
  }
}

/**
 * Converts rooms string to configuration object
 * @param rooms - Rooms string representation
 * @returns Room configuration object
 */
export const roomsToConfigurationObject = (
  rooms: string
): RoomConfigurationType => {
  const roomConfig = pipe(
    split(ROOM_SEPARATOR),
    map(roomToRoomSplit)
  )(rooms || DEFAULT_ROOMS)

  return roomsSplitToConfigurationObject(roomConfig)
}

const roomSplitToString = (roomSplit: RoomSplitType): string =>
  roomSplit.children.length > 0
    ? `${roomSplit.adults}:` + roomSplit.children.join(CHILD_SEPARATOR)
    : roomSplit.adults.toString()

/**
 * Converts rooms split array to string representation
 * @param roomsSplit - Array of room splits
 * @returns String representation of rooms
 */
export const roomsSplitToString = (
  roomsSplit: readonly RoomSplitType[]
): string => roomsSplit.map(roomSplitToString).join(ROOM_SEPARATOR)

/**
 * Gets the number of rooms from rooms string
 * @param rooms - Rooms string representation
 * @returns Number of rooms
 */
export const numberOfRooms = (rooms: string): number =>
  roomsToConfigurationObject(rooms).numberOfRooms

/**
 * Gets the total number of guests from rooms string
 * @param rooms - Rooms string representation
 * @returns Total number of guests
 */
export const numberOfGuests = (rooms: string): number =>
  roomsToConfigurationObject(rooms).numberOfAdults +
  roomsToConfigurationObject(rooms).numberOfChildren

/**
 * Adds unique IDs to rooms split array
 * @param roomsSplit - Array of room splits
 * @param generateId - Function to generate unique IDs
 * @returns Array of room splits with unique IDs
 */
export const addIdsToRoomsSplit = (
  roomsSplit: RoomSplitType[] = [],
  generateId: () => string = uuid
): UniqueRoomSplitType[] =>
  roomsSplit.map(
    (roomSplit: RoomSplitType): UniqueRoomSplitType => ({
      ...roomSplit,
      id: generateId()
    })
  )

/**
 * Removes IDs from rooms split array
 * @param roomsSplit - Array of room splits with or without IDs
 * @returns Array of room splits without IDs
 */
export const removeIdsFromRoomsSplit = (
  roomsSplit: UniqueRoomSplitType[] | RoomSplitType[]
): RoomSplitType[] =>
  roomsSplit.map(
    ({
      adults,
      children
    }: UniqueRoomSplitType | RoomSplitType): RoomSplitType => ({
      adults,
      children
    })
  )

/**
 * Flattens multiple rooms into a single room with guest limits
 * @param params - Object containing roomsSplit and maxGuests
 * @param params.roomsSplit - Array of room splits to flatten
 * @param params.maxGuests - Maximum number of guests allowed
 * @returns Array with single room split respecting guest limits
 */
export const flattenRoomsSplitToSingleRoom = ({
  roomsSplit,
  maxGuests
}: {
  roomsSplit: RoomSplitType[]
  maxGuests: number
}): RoomSplitType[] => {
  if (roomsSplit === undefined || roomsSplit.length === 0) return []

  const reducedRoomsSplit = [
    roomsSplit.reduce<RoomSplitType>(
      (acc, roomSplit) => ({
        adults: acc.adults + roomSplit.adults,
        children: [...acc.children, ...roomSplit.children]
      }),
      {adults: 0, children: []}
    )
  ]

  const allowedChildren = maxGuests - reducedRoomsSplit[0].adults

  if (reducedRoomsSplit[0].adults > maxGuests) {
    return [{...reducedRoomsSplit[0], adults: maxGuests}]
  }

  if (reducedRoomsSplit[0].children.length > allowedChildren) {
    return [
      {
        ...reducedRoomsSplit[0],
        children: reducedRoomsSplit[0].children.slice(0, allowedChildren)
      }
    ]
  }

  return reducedRoomsSplit
}

/**
 * Calculates total number of guests in a room split
 * @param roomSplit - Room split object
 * @returns Total number of guests
 */
export const totalNumberOfGuestsPerRoom = (roomSplit: RoomSplitType) => {
  return roomSplit.adults + roomSplit.children.length
}

/**
 * Converts rooms array to rooms split format
 * @param rooms - Array of Room or BoVioRoom objects
 * @returns Array of room splits with adults and children
 */
export const roomsToRoomsSplit = (rooms: Room[] | BoVioRoom[]) =>
  rooms.map(room => ({
    adults: room?.guests?.adults ?? DEFAULT_ADULTS_PER_ROOM,
    children: room?.guests?.childAges ?? []
  }))

/**
 * Converts rooms array to string representation
 * @param rooms - Array of Room or BoVioRoom objects
 * @returns String representation of rooms
 */
export const roomsToString = <T extends Room[] | BoVioRoom[]>(
  rooms: T
): string => {
  const roomsSplit = roomsToRoomsSplit(rooms)
  return roomsSplitToString(roomsSplit)
}

/**
 * Spreads adults across multiple rooms with default capacity
 * @param roomsSplit - Array of room splits
 * @returns Array of room splits with adults spread across rooms
 */
export const spreadAdultsIntoRooms = (
  roomsSplit: RoomSplitType[]
): RoomSplitType[] => {
  const totalAdults = roomsSplit.reduce((acc, roomSplit) => {
    return acc + roomSplit.adults
  }, 0)

  const spreadRoomsSplit = []
  let remainingValue = totalAdults

  while (remainingValue > 0) {
    const roomAdults = Math.min(DEFAULT_ADULTS_PER_ROOM, remainingValue)
    spreadRoomsSplit.push({adults: roomAdults, children: []})
    remainingValue -= roomAdults
  }

  return spreadRoomsSplit
}

/**
 * Ensures rooms split guest count is within valid limits
 * @param params - Object containing roomsSplit and maxGuests
 * @param params.roomsSplit - Array of room splits to validate
 * @param params.maxGuests - Maximum number of guests allowed
 * @returns Validated array of room splits
 */
export const ensureRoomsSplitGuestCountIsValid = ({
  roomsSplit,
  maxGuests
}: {
  roomsSplit: RoomSplitType[]
  maxGuests: number
}): RoomSplitType[] => {
  const validManualRoomsSplit = roomsSplit.map(roomSplit => {
    const totalGuests = totalNumberOfGuestsPerRoom(roomSplit)
    const hasTooManyGuests = totalGuests > maxGuests
    return hasTooManyGuests ? spreadAdultsIntoRooms([roomSplit]) : roomSplit
  })
  return flatten(validManualRoomsSplit)
}
